import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchProject,
  approveHyperDuskPreview,
  completeImageOnlyProject,
  consumeCreditsForProject,
  generateHyperDuskAnimation,
  generateHyperDuskPreview,
  pollHyperDuskStatus,
  saveEditedDuskVariant,
  saveEditedDuskVariantFromUrl,
  upgradeImageProjectToVideo,
  deleteProject as deleteHyperDuskProject,
  applyHyperDuskProvenance,
  supabase,
} from '../services/supabase';
import {
  installPhotoEditReturnListener,
  openPhotoEditHandoff,
  takePendingEditedHandoff,
} from '../services/photoedit-handoff';
import type {
  HyperDuskAnimationStyle,
  HyperDuskProject,
  HyperDuskProjectImage,
  HyperDuskVariant,
} from '../types';
import {
  DEFAULT_HYPER_DUSK_ANIMATION_STYLE,
  HYPER_DUSK_ANIMATION_STYLES,
  HYPER_DUSK_IMAGE_TO_VIDEO_CREDITS,
  HYPER_DUSK_MAX_IMAGE_VARIANTS,
} from '../types';
import { AIContentDisclosure } from '@rehub/ai-transparency';
import StatusTracker from './StatusTracker';
import PolishNightModal from './PolishNightModal';
import { ArrowLeft, Loader2, Check, Trash2, Download, ShieldAlert, Scissors, Film, SlidersHorizontal, ExternalLink, Camera } from 'lucide-react';
import { sendBackToPhotoplan, type PhotoplanReturnContext } from '../services/photoplan-return';

const PROPERTYCUT_APP_URL = 'https://propertycuts.rehub.studio';

/**
 * Open PropertyCuts with a deep-link to import this Hyper Dusk asset as a
 * fresh draft project. Pass `imageId` to import a single dusk-transformed
 * still; omit it to import the project's final timelapse video.
 */
async function openPropertyCutWithHyperDusk(args: {
  projectId: string;
  imageId?: string;
}): Promise<void> {
  const params = new URLSearchParams({
    source: 'hyperdusk',
    projectId: args.projectId,
  });
  if (args.imageId) params.set('imageId', args.imageId);
  let hash = '';
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token && session?.refresh_token) {
      hash =
        `#sso_access_token=${encodeURIComponent(session.access_token)}` +
        `&sso_refresh_token=${encodeURIComponent(session.refresh_token)}`;
    }
  } catch {
    // Fall through; PropertyCuts will prompt for login if SSO can't be forwarded.
  }
  const url = `${PROPERTYCUT_APP_URL}/?${params.toString()}${hash}`;
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  if (!win || win.closed) {
    window.location.href = url;
  }
}

type AppView = 'LOGIN' | 'DASHBOARD' | 'NEW_PROJECT' | 'PROJECT_DETAIL';

interface ProjectDetailProps {
  projectId: string;
  onNavigate: (view: AppView) => void;
  currentUserId?: string | null;
  userCredits?: number;
  onCreditsUpdated?: (credits: number) => void;
  photoplanReturn?: PhotoplanReturnContext | null;
}

export default function ProjectDetail({
  projectId,
  onNavigate,
  currentUserId,
  userCredits = 0,
  onCreditsUpdated,
  photoplanReturn,
}: ProjectDetailProps) {
  const logoSrc = '/hyperdusk-logo-wordmark-alpha.png';
  const reviewSectionRef = useRef<HTMLDivElement>(null);
  const [project, setProject] = useState<HyperDuskProject | null>(null);
  const [images, setImages] = useState<HyperDuskProjectImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [animationDurationSeconds, setAnimationDurationSeconds] = useState(5);
  const [animationStyle, setAnimationStyle] = useState<HyperDuskAnimationStyle>(DEFAULT_HYPER_DUSK_ANIMATION_STYLE);
  const [regeneratingPreview, setRegeneratingPreview] = useState(false);
  const [approvingPreview, setApprovingPreview] = useState(false);
  const [generatingAnimation, setGeneratingAnimation] = useState(false);
  const [finishingImage, setFinishingImage] = useState(false);
  const [upgradingToVideo, setUpgradingToVideo] = useState(false);
  const [deletingProject, setDeletingProject] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [downloadingUrl, setDownloadingUrl] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showPolishModal, setShowPolishModal] = useState(false);
  const duskProvenanceAppliedRef = useRef<Set<string>>(new Set());

  const hyperDuskImage = images[0] ?? null;
  const variants = (hyperDuskImage?.dusk_variants ?? []) as HyperDuskVariant[];
  const selectedVariant = variants.find((variant) => variant.id === selectedVariantId) ?? variants[variants.length - 1] ?? null;
  const sourcePreview = hyperDuskImage?.source_image_url || hyperDuskImage?.image_url || null;
  const duskPreview = selectedVariant?.url || hyperDuskImage?.dusk_image_url || null;
  const generationStatus = hyperDuskImage?.generation_status ?? null;
  const hasDuskPreview = !!duskPreview;
  const isPortraitPreview = project?.settings?.aspect_ratio === '9:16';

  const outputMode = project?.settings?.output_mode ?? 'video';
  const isImageMode = outputMode === 'image';
  const variantLimitReached = isImageMode && variants.length >= HYPER_DUSK_MAX_IMAGE_VARIANTS;

  const isAdminView = !!(currentUserId && project && project.user_id !== currentUserId);

  // A finished image-only project can be upgraded into the full video.
  const canUpgradeToVideo =
    isImageMode &&
    !isAdminView &&
    hasDuskPreview &&
    (project?.status === 'completed' || generationStatus === 'completed');
  const hasEnoughUpgradeCredits = userCredits >= HYPER_DUSK_IMAGE_TO_VIDEO_CREDITS;

  const canApprove =
    !isImageMode &&
    generationStatus === 'awaiting_user_approval' &&
    hasDuskPreview &&
    !approvingPreview &&
    !generatingAnimation;
  const canGenerateAnimation =
    !isImageMode &&
    generationStatus === 'dusk_ready' &&
    hasDuskPreview &&
    !generatingAnimation &&
    project?.status !== 'rendering';

  const load = useCallback(async () => {
    const result = await fetchProject(projectId);
    if (!result.project) {
      setError('Project not found');
      setLoading(false);
      return;
    }
    setProject(result.project);
    setImages(result.images);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!projectId || !currentUserId) return;
    return installPhotoEditReturnListener((payload) => {
      if (payload.projectId !== projectId) return;
      void (async () => {
        try {
          const { variantId } = await saveEditedDuskVariantFromUrl({
            projectId: payload.projectId,
            imageId: payload.imageId,
            userId: currentUserId,
            imageUrl: payload.imageUrl,
          });
          setSelectedVariantId(variantId);
          await load();
        } catch (err) {
          setActionError(err instanceof Error ? err.message : 'Failed to import edited image from PhotoEdit');
        }
      })();
    });
  }, [projectId, currentUserId, load]);

  // Deep-link return from PhotoEdit when the Hyper Dusk tab was closed.
  useEffect(() => {
    if (!projectId || !currentUserId) return;
    const pending = takePendingEditedHandoff(projectId);
    if (!pending) return;
    void (async () => {
      try {
        const { variantId } = await saveEditedDuskVariantFromUrl({
          projectId: pending.projectId,
          imageId: pending.imageId,
          userId: currentUserId,
          imageUrl: pending.imageUrl,
        });
        setSelectedVariantId(variantId);
        await load();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Failed to import edited image from PhotoEdit');
      }
    })();
  }, [projectId, currentUserId, load]);

  useEffect(() => {
    if (!project) return;
    if (['completed', 'failed', 'expired'].includes(project.status)) return;
    const interval = setInterval(async () => {
      const image = images[0];
      // Drive the poll worker whenever there is work it could pick up:
      // - kling_processing: fal.ai is rendering the animation
      // - animation_ready + no final_video_url: the worker still needs to
      //   finalize the project (covers legacy stuck-at-"Animation Ready"
      //   projects where the old imagemotion-render trigger timed out)
      const needsServerPoll =
        image?.generation_status === 'kling_processing' ||
        (image?.generation_status === 'animation_ready' && !project.final_video_url);

      if (needsServerPoll) {
        try {
          const pollResult = await pollHyperDuskStatus(projectId);
          if (pollResult.error) {
            console.warn('Hyper Dusk poll error:', pollResult.error);
          }
          const failedResults = pollResult.results?.filter((r) => !!r.error);
          if (failedResults?.length) {
            setActionError(failedResults.map((r) => r.error).join('; '));
          }
        } catch (pollErr) {
          console.warn('Hyper Dusk poll call failed:', pollErr);
        }
      }

      await load();
    }, 5000);
    return () => clearInterval(interval);
  }, [project?.status, project?.final_video_url, projectId, images, load]);

  useEffect(() => {
    if (!hyperDuskImage) return;
    const nextVariantId =
      hyperDuskImage.selected_dusk_variant_id ||
      variants[variants.length - 1]?.id ||
      null;
    setSelectedVariantId((current) => current ?? nextVariantId);
  }, [hyperDuskImage?.id, hyperDuskImage?.selected_dusk_variant_id, variants]);

  // Persist dusk provenance once when the still becomes ready for review.
  useEffect(() => {
    if (!hyperDuskImage?.id) return;
    const ready =
      generationStatus === 'awaiting_user_approval' ||
      generationStatus === 'dusk_ready' ||
      generationStatus === 'completed';
    if (!ready || !hasDuskPreview) return;
    if (duskProvenanceAppliedRef.current.has(hyperDuskImage.id)) return;
    duskProvenanceAppliedRef.current.add(hyperDuskImage.id);
    void applyHyperDuskProvenance(hyperDuskImage.id, {
      sourceAssetId: hyperDuskImage.source_image_path || hyperDuskImage.source_image_url || hyperDuskImage.image_url,
      outputAssetId: selectedVariant?.path || hyperDuskImage.dusk_image_path || hyperDuskImage.dusk_image_url,
      operation: 'hyperdusk_dusk',
    });
  }, [
    hyperDuskImage?.id,
    generationStatus,
    hasDuskPreview,
    hyperDuskImage?.source_image_path,
    hyperDuskImage?.source_image_url,
    hyperDuskImage?.image_url,
    hyperDuskImage?.dusk_image_path,
    hyperDuskImage?.dusk_image_url,
    selectedVariant?.path,
  ]);

  // Seed duration/motion from the DB when opening a project. Do not re-sync
  // on every poll refresh while still at dusk_ready — clip_duration stays at
  // the create-time default (5s) until animation runs, so overwriting here
  // would discard the user's 10s pick before they click Generate.
  useEffect(() => {
    if (!hyperDuskImage) return;
    setAnimationDurationSeconds(
      Math.max(5, Math.min(10, Math.round(hyperDuskImage.clip_duration || 5)))
    );
    if (hyperDuskImage.kling_animation_style) {
      setAnimationStyle(hyperDuskImage.kling_animation_style);
    }
  }, [hyperDuskImage?.id]);

  useEffect(() => {
    if (!hyperDuskImage) return;
    const lockedStatuses = new Set(['kling_processing', 'animation_ready', 'completed']);
    if (!lockedStatuses.has(hyperDuskImage.generation_status ?? '')) return;
    setAnimationDurationSeconds(
      Math.max(5, Math.min(10, Math.round(hyperDuskImage.clip_duration || 5)))
    );
    if (hyperDuskImage.kling_animation_style) {
      setAnimationStyle(hyperDuskImage.kling_animation_style);
    }
  }, [
    hyperDuskImage?.clip_duration,
    hyperDuskImage?.generation_status,
    hyperDuskImage?.kling_animation_style,
  ]);

  const handleRegeneratePreview = async () => {
    setRegeneratingPreview(true);
    setActionError(null);
    try {
      await generateHyperDuskPreview(projectId);
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to regenerate dusk preview');
    } finally {
      setRegeneratingPreview(false);
    }
  };

  const handleApprovePreview = async () => {
    if (!hyperDuskImage?.id) return;
    setApprovingPreview(true);
    setActionError(null);
    try {
      await approveHyperDuskPreview(projectId, hyperDuskImage.id, selectedVariant?.id);
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to approve dusk preview');
    } finally {
      setApprovingPreview(false);
    }
  };

  const handleSavePolish = async (file: File) => {
    if (!hyperDuskImage || !currentUserId) return;
    const { variantId } = await saveEditedDuskVariant({
      projectId,
      imageId: hyperDuskImage.id,
      userId: currentUserId,
      file,
    });
    setSelectedVariantId(variantId);
    await load();
  };

  const openInPhotoEdit = () => {
    if (!hyperDuskImage || !duskPreview) return;
    openPhotoEditHandoff({
      imageUrl: duskPreview,
      projectId,
      imageId: hyperDuskImage.id,
    });
  };

  const handleGenerateAnimation = async () => {
    if (!hasDuskPreview) return;
    setGeneratingAnimation(true);
    setActionError(null);
    try {
      await generateHyperDuskAnimation(projectId, {
        durationSeconds: animationDurationSeconds,
        animationStyle,
        selectedVariantId: selectedVariant?.id,
      });
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to generate animation');
    } finally {
      setGeneratingAnimation(false);
    }
  };

  const handleFinishImage = async () => {
    if (finishingImage) return;
    setFinishingImage(true);
    setActionError(null);
    try {
      await completeImageOnlyProject(projectId, hyperDuskImage?.id);
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to save evening image');
    } finally {
      setFinishingImage(false);
    }
  };

  const handleUpgradeToVideo = async () => {
    if (upgradingToVideo || !project || !hyperDuskImage?.id) return;
    if (currentUserId && project.user_id !== currentUserId) return;
    if (userCredits < HYPER_DUSK_IMAGE_TO_VIDEO_CREDITS) {
      setActionError(`Not enough credits. You need ${HYPER_DUSK_IMAGE_TO_VIDEO_CREDITS} credits to add a video.`);
      return;
    }
    setUpgradingToVideo(true);
    setActionError(null);
    try {
      const creditResult = await consumeCreditsForProject(
        project.user_id,
        projectId,
        HYPER_DUSK_IMAGE_TO_VIDEO_CREDITS
      );
      if (!creditResult.success) {
        setActionError(
          creditResult.error === 'INSUFFICIENT_CREDITS'
            ? `Not enough credits. You need ${HYPER_DUSK_IMAGE_TO_VIDEO_CREDITS} credits to add a video.`
            : 'Failed to process credits. Please try again.'
        );
        return;
      }
      if (creditResult.credits_remaining !== undefined) {
        onCreditsUpdated?.(creditResult.credits_remaining);
      }
      // Re-open the project for the video flow, then approve the chosen
      // dusk variant so it lands in `dusk_ready` for the animation step.
      await upgradeImageProjectToVideo(projectId);
      await approveHyperDuskPreview(projectId, hyperDuskImage.id, selectedVariant?.id);
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to upgrade to video');
    } finally {
      setUpgradingToVideo(false);
    }
  };

  const handleDownload = async (url: string, filename: string) => {
    if (downloadingUrl) return;
    setDownloadingUrl(url);
    setActionError(null);
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloadingUrl(null);
    }
  };

  // A `failed` Hyper Dusk project is frequently NOT a real generation
  // failure — fal.ai often finished the clip, but a transient error in the
  // handoff (downloading the clip / storing it / finalizing) stamped the
  // project failed. The poll worker can re-check fal.ai and finalize from
  // the already-completed job (no re-run, no extra credits), so expose a
  // manual retry that drives it. The normal poll loop skips `failed`, so
  // this button is the only thing that can kick recovery off.
  const handleRetry = async () => {
    if (retrying) return;
    setRetrying(true);
    setActionError(null);
    try {
      const pollResult = await pollHyperDuskStatus(projectId);
      if (pollResult.error) {
        setActionError(pollResult.error);
      } else {
        const failed = pollResult.results?.filter((r) => !!r.error);
        if (failed?.length) setActionError(failed.map((r) => r.error).join('; '));
      }
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to retry project');
    } finally {
      setRetrying(false);
    }
  };

  const handleDeleteProject = async () => {
    if (deletingProject) return;
    const confirmed = window.confirm('Delete this Hyper Dusk project? This cannot be undone.');
    if (!confirmed) return;
    setDeletingProject(true);
    setActionError(null);
    try {
      await deleteHyperDuskProject(projectId);
      onNavigate('DASHBOARD');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete project');
    } finally {
      setDeletingProject(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="text-center py-20">
        <p className="text-red-400">{error ?? 'Project not found'}</p>
        <button
          onClick={() => onNavigate('DASHBOARD')}
          className="inline-flex items-center gap-2 mt-4 text-blue-400 hover:underline"
        >
          <ArrowLeft className="w-4 h-4" /> Back to projects
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8 animate-fade-in">
      <div>
        <button
          onClick={() => onNavigate('DASHBOARD')}
          className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-blue-400 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> All Projects
        </button>
        <div className="flex items-center justify-between">
          <div>
            <img src={logoSrc} alt="Hyper Dusk" className="h-7 w-auto mb-2" />
            <h1 className="text-2xl font-bold text-white">{project.name || 'Untitled Project'}</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">Created {new Date(project.created_at).toLocaleDateString()}</span>
            <button
              type="button"
              onClick={handleDeleteProject}
              disabled={deletingProject}
              className="inline-flex items-center gap-1.5 rounded-md border border-red-700/70 bg-red-900/30 px-3 py-1.5 text-xs font-semibold text-red-300 hover:bg-red-900/50 disabled:opacity-50 transition-colors"
            >
              {deletingProject ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              {deletingProject ? 'Deleting...' : 'Delete Project'}
            </button>
          </div>
        </div>
      </div>

      {isAdminView && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-600/40 bg-amber-900/20 px-4 py-3">
          <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-300">Admin View</p>
            <p className="text-xs text-amber-400/70">This project belongs to another user. You're viewing it as an admin.</p>
          </div>
        </div>
      )}

      <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
        <h2 className="text-sm font-medium text-slate-400 mb-4">Processing Status</h2>
        <StatusTracker
          status={project.status}
          errorMessage={project.error_message}
          generationStatus={images[0]?.generation_status ?? null}
          outputMode={outputMode}
        />
        {project.status === 'failed' && !isImageMode && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-700 bg-slate-950/60 px-4 py-3">
            <p className="text-xs text-slate-400">
              If the clip finished rendering on the provider, retrying re-checks it and finalizes your video — no re-run and no extra credits.
            </p>
            <button
              type="button"
              onClick={handleRetry}
              disabled={retrying}
              className="inline-flex shrink-0 items-center gap-2 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {retrying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Film className="h-3.5 w-3.5" />}
              {retrying ? 'Retrying...' : 'Retry / Recover'}
            </button>
          </div>
        )}
        {isImageMode && generationStatus === 'awaiting_user_approval' && (
          <div className="mt-4 rounded-lg border border-indigo-500/30 bg-indigo-900/20 px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-indigo-200">Evening image is ready. Review and download below.</p>
              <button
                type="button"
                onClick={() => reviewSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="shrink-0 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500 transition-colors"
              >
                Review Evening Image
              </button>
            </div>
          </div>
        )}

        {!isImageMode &&
          (generationStatus === 'awaiting_user_approval' ||
            generationStatus === 'dusk_ready' ||
            generationStatus === 'kling_processing') && (
          <div className="mt-4 rounded-lg border border-indigo-500/30 bg-indigo-900/20 px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-indigo-200">
                {generationStatus === 'awaiting_user_approval'
                  ? 'Night preview is ready. Review and approve below.'
                  : 'Night version approved. Continue below to generate animation.'}
              </p>
              <button
                type="button"
                onClick={() => reviewSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="shrink-0 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500 transition-colors"
              >
                {generationStatus === 'awaiting_user_approval' ? 'Review Night Version' : 'Open Animation Controls'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div ref={reviewSectionRef} className="bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-medium text-slate-400">
            {isImageMode ? 'Your Evening Image' : 'Review & Approve Night Version'}
          </h2>
          {hasDuskPreview && (
            <AIContentDisclosure
              classification="AI_MODIFIED"
              operation="hyperdusk_dusk"
              app="hyperdusk"
            />
          )}
        </div>

        {actionError && (
          <div className="p-3 rounded-lg bg-red-900/20 border border-red-900/50 text-red-300 text-sm">
            {actionError}
          </div>
        )}

        <div
          className={`grid gap-4 ${
            isPortraitPreview
              ? 'grid-cols-1 sm:grid-cols-2 max-w-3xl mx-auto'
              : 'grid-cols-1 md:grid-cols-2'
          }`}
        >
          <div className="rounded-lg border border-slate-700 bg-slate-950 p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-wide text-slate-400">Original</p>
              {sourcePreview && (
                <button
                  type="button"
                  onClick={() => handleDownload(sourcePreview, `${project.name || 'hyperdusk'}-original.png`)}
                  disabled={downloadingUrl === sourcePreview}
                  className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-slate-300 bg-slate-800 rounded hover:bg-slate-700 transition-colors disabled:opacity-70 disabled:cursor-wait"
                >
                  {downloadingUrl === sourcePreview ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Download className="w-3 h-3" />
                  )}
                  {downloadingUrl === sourcePreview ? 'Downloading…' : 'Download'}
                </button>
              )}
            </div>
            <div className="rounded bg-slate-900 flex items-center justify-center overflow-hidden min-h-[12rem]">
              {sourcePreview ? (
                <img
                  src={sourcePreview}
                  alt="Original upload"
                  className="max-h-[70vh] w-full object-contain"
                />
              ) : (
                <span className="text-xs text-slate-500 py-16">No source image</span>
              )}
            </div>
          </div>
          <div className="rounded-lg border border-slate-700 bg-slate-950 p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-wide text-slate-400">{isImageMode ? 'Evening Image' : 'Night Version'}</p>
              {duskPreview && (
                <div className="flex items-center gap-1">
                  {hyperDuskImage && !isAdminView && (
                    <button
                      type="button"
                      onClick={() => openPropertyCutWithHyperDusk({ projectId: project.id, imageId: hyperDuskImage.id })}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-slate-300 bg-slate-800 rounded hover:bg-teal-900/40 hover:text-teal-200 transition-colors"
                      title="Send to PropertyCuts as a photo"
                    >
                      <Scissors className="w-3 h-3" />
                      To PropertyCuts
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDownload(duskPreview, `${project.name || 'hyperdusk'}-night.png`)}
                    disabled={downloadingUrl === duskPreview}
                    className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-slate-300 bg-slate-800 rounded hover:bg-slate-700 transition-colors disabled:opacity-70 disabled:cursor-wait"
                  >
                    {downloadingUrl === duskPreview ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Download className="w-3 h-3" />
                    )}
                    {downloadingUrl === duskPreview ? 'Downloading…' : 'Download'}
                  </button>
                  {photoplanReturn && (
                    <button
                      type="button"
                      onClick={() => sendBackToPhotoplan(photoplanReturn, duskPreview)}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-sky-600 rounded hover:bg-sky-500 transition-colors"
                      title={
                        photoplanReturn.returnPath
                          ? 'Save this night version to the Photoplan gallery'
                          : 'Send this night version back to Photoplan QC'
                      }
                    >
                      <Camera className="w-3 h-3" />
                      {photoplanReturn.returnPath ? 'Save to gallery' : 'To Photoplan'}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="rounded bg-slate-900 flex items-center justify-center overflow-hidden min-h-[12rem]">
              {duskPreview ? (
                <img
                  src={duskPreview}
                  alt="Generated night version"
                  className="max-h-[70vh] w-full object-contain"
                />
              ) : (
                <span className="text-xs text-slate-500 py-16">Generating preview...</span>
              )}
            </div>
          </div>
        </div>

        {variants.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide text-slate-400">Saved Variants ({variants.length})</p>
              <p className="text-xs text-slate-500">Select the version to approve or animate</p>
            </div>
            <div
              className={`grid gap-3 ${
                isPortraitPreview
                  ? 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6'
                  : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4'
              }`}
            >
              {variants.map((variant, index) => {
                const isSelected = variant.id === selectedVariant?.id;
                return (
                  <button
                    key={variant.id}
                    type="button"
                    onClick={() => setSelectedVariantId(variant.id)}
                    className={`rounded-lg border p-2 text-left transition-colors ${
                      isSelected
                        ? 'border-indigo-500 bg-indigo-900/20'
                        : 'border-slate-700 bg-slate-800/40 hover:border-slate-600'
                    }`}
                  >
                    <div
                      className={`rounded overflow-hidden bg-slate-900 flex items-center justify-center w-full ${
                        isPortraitPreview ? 'aspect-[9/16] max-h-48 mx-auto' : ''
                      }`}
                    >
                      {variant.url ? (
                        <img
                          src={variant.url}
                          alt={`Variant ${index + 1}`}
                          className={
                            isPortraitPreview
                              ? 'max-h-full max-w-full object-contain'
                              : 'max-h-40 w-full object-contain'
                          }
                        />
                      ) : (
                        <span className="text-[10px] text-slate-500 py-8">No preview</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-300 mt-2 font-medium">Variant {index + 1}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {isImageMode && generationStatus === 'awaiting_user_approval' && (
          <div className="space-y-3 border-t border-slate-800 pt-4">
            <p className="text-xs text-slate-400">
              {variantLimitReached
                ? `Variant limit reached (${variants.length} of ${HYPER_DUSK_MAX_IMAGE_VARIANTS}). Download the version you like best, then save.`
                : `You can generate up to ${HYPER_DUSK_MAX_IMAGE_VARIANTS} variants (${variants.length} of ${HYPER_DUSK_MAX_IMAGE_VARIANTS} used). Download the version you like best, then save.`}
            </p>
            <div className="flex flex-wrap items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleRegeneratePreview}
                disabled={regeneratingPreview || finishingImage || variantLimitReached}
                className="px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {regeneratingPreview ? 'Regenerating...' : 'Regenerate'}
              </button>
              <button
                type="button"
                onClick={handleFinishImage}
                disabled={finishingImage || regeneratingPreview || !hasDuskPreview}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {finishingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {finishingImage ? 'Saving...' : 'Save Evening Image'}
              </button>
            </div>
          </div>
        )}

        {!isImageMode && generationStatus === 'awaiting_user_approval' && (
          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-800 pt-4">
            <button
              type="button"
              onClick={() => setShowPolishModal(true)}
              disabled={!duskPreview || regeneratingPreview || approvingPreview}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Polish
            </button>
            <button
              type="button"
              onClick={openInPhotoEdit}
              disabled={!duskPreview || regeneratingPreview || approvingPreview || isAdminView}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Open in PhotoEdit
            </button>
            <button
              type="button"
              onClick={handleRegeneratePreview}
              disabled={regeneratingPreview || approvingPreview || generatingAnimation}
              className="px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors"
            >
              {regeneratingPreview ? 'Regenerating...' : 'Regenerate Night Version'}
            </button>
            <button
              type="button"
              onClick={handleApprovePreview}
              disabled={!canApprove}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {approvingPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {approvingPreview ? 'Approving...' : 'Approve Night Version'}
            </button>
          </div>
        )}

        {!isImageMode && (generationStatus === 'dusk_ready' || generationStatus === 'kling_processing') && (
          <div className="space-y-4 border-t border-slate-800 pt-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">Animation Settings</p>
                <p className="text-xs text-slate-500 mt-1">
                  Polish the night still if needed, then pick duration and camera motion for Kling.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowPolishModal(true)}
                  disabled={!duskPreview || generatingAnimation || generationStatus === 'kling_processing'}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors"
                >
                  <SlidersHorizontal className="w-3.5 h-3.5" />
                  Polish
                </button>
                <button
                  type="button"
                  onClick={openInPhotoEdit}
                  disabled={!duskPreview || generatingAnimation || generationStatus === 'kling_processing' || isAdminView}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  PhotoEdit
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-4 items-start">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-2">Duration</label>
                <div className="flex flex-wrap gap-2">
                  {[5, 6, 7, 8, 9, 10].map((duration) => {
                    const isSelected = duration === animationDurationSeconds;
                    return (
                      <button
                        key={duration}
                        type="button"
                        onClick={() => setAnimationDurationSeconds(duration)}
                        disabled={generatingAnimation || generationStatus === 'kling_processing'}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                          isSelected
                            ? 'border-indigo-500 bg-indigo-900/40 text-white'
                            : 'border-slate-700 bg-slate-800/40 text-slate-300 hover:border-slate-600'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {duration}s
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-2">Camera Motion</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {HYPER_DUSK_ANIMATION_STYLES.map((style) => {
                    const isSelected = style.value === animationStyle;
                    return (
                      <button
                        key={style.value}
                        type="button"
                        onClick={() => setAnimationStyle(style.value)}
                        disabled={generatingAnimation || generationStatus === 'kling_processing'}
                        className={`p-2.5 rounded-lg border text-left transition-colors ${
                          isSelected
                            ? 'border-indigo-500 bg-indigo-900/30'
                            : 'border-slate-700 bg-slate-800/40 hover:border-slate-600'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <p className="text-xs font-semibold text-white">{style.label}</p>
                        <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">{style.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <div className="text-xs text-slate-400">
                Will render a <span className="font-semibold text-slate-200">{animationDurationSeconds}s</span> clip with
                {' '}<span className="font-semibold text-slate-200">
                  {HYPER_DUSK_ANIMATION_STYLES.find((s) => s.value === animationStyle)?.label ?? animationStyle}
                </span>.
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleRegeneratePreview}
                  disabled={regeneratingPreview || approvingPreview || generatingAnimation || generationStatus === 'kling_processing'}
                  className="px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors"
                >
                  {regeneratingPreview ? 'Regenerating...' : 'Regenerate Night Version'}
                </button>
                <button
                  type="button"
                  onClick={handleGenerateAnimation}
                  disabled={!canGenerateAnimation}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:cursor-not-allowed ${
                    generatingAnimation || generationStatus === 'kling_processing'
                      ? 'bg-indigo-600 ring-2 ring-indigo-400/60 shadow-lg shadow-indigo-500/30 animate-pulse'
                      : 'bg-green-600 hover:bg-green-700 disabled:opacity-50'
                  }`}
                >
                  {generatingAnimation || generationStatus === 'kling_processing' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating Animation...
                    </>
                  ) : (
                    `Generate ${animationDurationSeconds}s Animation`
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {canUpgradeToVideo && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-slate-800 pt-4">
            <div>
              <p className="text-sm font-medium text-white">Want this as a cinematic video?</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Animate this evening image into a day-to-night timelapse for {HYPER_DUSK_IMAGE_TO_VIDEO_CREDITS} extra credits.
                {' '}<span className="text-slate-500">Balance: {userCredits} credits</span>
              </p>
            </div>
            <button
              type="button"
              onClick={handleUpgradeToVideo}
              disabled={upgradingToVideo || !hasEnoughUpgradeCredits}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
            >
              {upgradingToVideo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />}
              {upgradingToVideo
                ? 'Upgrading...'
                : `Animate to Video (${HYPER_DUSK_IMAGE_TO_VIDEO_CREDITS} credits)`}
            </button>
          </div>
        )}
      </div>

      {project.final_video_url && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-white">Final Video</h2>
              <AIContentDisclosure
                classification="AI_GENERATED"
                operation="hyperdusk_animation"
                app="hyperdusk"
              />
            </div>
            <div className="flex items-center gap-2">
              {!isAdminView && (
                <button
                  type="button"
                  onClick={() => openPropertyCutWithHyperDusk({ projectId: project.id })}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-500 transition-colors"
                  title="Send this video to PropertyCuts as a clip"
                >
                  <Scissors className="w-3.5 h-3.5" />
                  Send to PropertyCuts
                </button>
              )}
              <button
                type="button"
                onClick={() => handleDownload(project.final_video_url!, `${project.name || 'hyperdusk'}-timelapse.mp4`)}
                disabled={downloadingUrl === project.final_video_url}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-500 transition-colors disabled:opacity-70 disabled:cursor-wait"
              >
                {downloadingUrl === project.final_video_url ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                {downloadingUrl === project.final_video_url ? 'Downloading…' : 'Download Video'}
              </button>
            </div>
          </div>
          <video src={project.final_video_url} controls className="w-full rounded-lg" />
        </div>
      )}

      {showPolishModal && duskPreview && (
        <PolishNightModal
          imageUrl={duskPreview}
          onClose={() => setShowPolishModal(false)}
          onSave={handleSavePolish}
        />
      )}
    </div>
  );
}
